// Location Settings screen: owns edit mode, saving and the post-save refresh, and
// composes the schema fields from the shared dynamic-form module.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { Grid, useNotifications } from '@decisiv/ui-components';
import noop from 'lodash/noop';

import {
  attributesToValues,
  isFieldFilled,
  isFormConfigured,
  isHeadersField,
  requiredNames,
  useDynamicForm,
} from '../../features/dynamic-form';
import { IntegrationForm } from '../StyledComponents';
import ConfiguredIntegrationsApi from '../../api/configured_integrations';
import CommandsApi from '../../api/commands';
import PropagateSettingsModal from '../PropagateSettingsModal';
import SchemaDetailsModal from '../SchemaDetailsModal';
import passwordMask from '../../utils/passwordMask';
import { buildSchemaContent } from '../../utils/buildSchemaContent';
import FormFooter from './FormFooter';
import SchemaSections from './SchemaSections';
import FormHeader from './FormHeader';
import dynamicFormTexts from './texts';
import { linkedSourceValues } from './linkedSourceValues';
import usePropagateSettings from './usePropagateSettings';

const asRows = (value) => (Array.isArray(value) ? value : []);
const isBlankText = (value) => !String(value ?? '').trim();

const Form = ({
  linkedSourceData = null,
  setLinkedSourceData = noop,
  ...props
}) => {
  const [edit, setEdit] = useState(true);
  const [disableSubmit, setDisableSubmit] = useState(true);
  const [additionalDetails, setAdditionalDetails] = useState(
    props.configuredIntegration.attributes.additional_details,
  );
  // The attributes as last persisted. The parent does not re-fetch after a save,
  // so this is what keeps the enable toggle in step with the server.
  const [savedAttributes, setSavedAttributes] = useState(
    props.locationSettingsAttributes,
  );
  const [schemasByCommand, setSchemasByCommand] = useState([]);
  const [schemasContent, setSchemasContent] = useState({
    input: '',
    output: '',
  });
  const [schemaDetailsModalVisibility, setSchemaDetailsModalVisibility] =
    useState(false);
  const [apiError, setApiError] = useState([]);
  // Cancel remounts the form, which brings the banner back with the saved values.
  const [linkedValuesLoaded, setLinkedValuesLoaded] = useState(false);
  const locationInitialValue = useRef();

  const { notify } = useNotifications();
  const texts = useMemo(() => dynamicFormTexts(), []);

  const form = useDynamicForm({
    schema: props.locationSettingsSchema,
    initialAttributes: props.locationSettingsAttributes,
  });

  const { reset, initJsonToggles, isConfigured, valuesValid } = form;

  const notifyResult = useCallback(
    (intent, body) => {
      notify(
        { intent, title: `Updated Integration Settings`, onClose: noop },
        body,
      );
    },
    [notify],
  );

  const inSharedSetting =
    !!props.integration?.id &&
    props.allSharedSettings.some((s) =>
      (s.integration_uuids || []).includes(props.integration.id),
    );

  const propagate = usePropagateSettings({
    configuredIntegration: props.configuredIntegration,
    integration: props.integration,
    allSharedSettings: props.allSharedSettings,
    onFetchFailed: useCallback(
      () => notifyResult('warning', `Failed to fetch related integrations.`),
      [notifyResult],
    ),
  });

  useEffect(() => {
    locationInitialValue.current = props.configuredIntegration;
    setSavedAttributes(props.locationSettingsAttributes);
    reset(props.locationSettingsAttributes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.locationSettingsAttributes]);

  // Whether the *saved* record is complete — deliberately not the live form,
  // which may hold unsaved edits.
  const savedConfigured = useMemo(
    () =>
      isFormConfigured(
        savedAttributes,
        requiredNames(props.locationSettingsSchema),
      ),
    [savedAttributes, props.locationSettingsSchema],
  );

  // Enabling a location takes effect on the server immediately, so it must only
  // be reachable once the required values are actually persisted. `edit` is true
  // when the form is read-only, so editing locks the toggle regardless.
  useEffect(() => {
    props.setToggleDisabled(!edit || !savedConfigured);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit, savedConfigured]);

  useEffect(() => {
    setAdditionalDetails(
      props.configuredIntegration.attributes.additional_details,
    );
  }, [props.configuredIntegration.attributes.additional_details]);

  useEffect(() => {
    async function getSchemasByCommand() {
      if (props.integration?.id) {
        setSchemasByCommand(
          await CommandsApi.fetchSchemasGroupedByCommand(
            props.integration.attributes.commands_flow,
          ),
        );
      }
    }

    getSchemasByCommand();
  }, [props.integration?.id, props.integration.attributes.commands_flow]);

  useEffect(() => {
    initJsonToggles(props.locationSettingsAttributes);
  }, [
    initJsonToggles,
    props.locationSettingsSchema,
    props.locationSettingsAttributes,
    edit,
  ]);

  // Runs on every keystroke as well as on blur, so the Save button keeps step
  // with what is typed. Validates the value just given rather than reading
  // state, which is still a keystroke behind inside this handler.
  // Whether the values allow a save is derived at render time. Computing it here
  // would read `isConfigured` and `valuesValid` from the render before this
  // keystroke, leaving the button a character behind.
  const validate = useCallback(
    (name, value) => form.validateValue(name, value),
    [form],
  );

  const validateField = useCallback(
    (name) => validate(name, form.values[name]),
    [validate, form.values],
  );

  const handleInputChange = useCallback(
    (name, value) => {
      form.setValue(name, value);
      validate(name, value);
    },
    [form, validate],
  );

  const handleHeaderValidate = useCallback(
    (inputName, isBlank) => {
      form.setHeaderError(inputName, isBlank);
      setDisableSubmit(isBlank);
    },
    [form],
  );

  // Applied as if typed, so each field revalidates and syncs its own JSON toggle.
  const loadLinkedValues = () => {
    const loaded = linkedSourceValues(
      form.values,
      linkedSourceData.attributesToLoad,
    );
    Object.keys(loaded).forEach((name) =>
      handleInputChange(name, loaded[name]),
    );

    const loadedHeaders = Object.keys(loaded).filter(isHeadersField);
    if (loadedHeaders.length > 0) {
      const draft = { ...form.values, ...loaded };
      const headerNames = Object.keys(draft).filter(isHeadersField);
      // Row errors are keyed 'key-0'/'value-0' for every headers field alike, so
      // a row is flagged while any headers field is blank at that position.
      const blankAt = (part, index) =>
        headerNames.some((name) => {
          const rows = asRows(draft[name]);
          return index < rows.length && isBlankText(rows[index]?.[part]);
        });

      loadedHeaders.forEach((name) => {
        const count = Math.max(
          asRows(loaded[name]).length,
          asRows(form.values[name]).length,
        );
        Array.from({ length: count }).forEach((_, index) => {
          form.setHeaderError(`key-${index}`, blankAt(0, index));
          form.setHeaderError(`value-${index}`, blankAt(1, index));
        });
      });
      setDisableSubmit(
        !headerNames.every((name) =>
          isFieldFilled({ name, value: draft[name] }),
        ),
      );
    }

    setLinkedValuesLoaded(true);
  };

  const handleEmailBlur = useCallback(
    (name) => setDisableSubmit(!form.blurEmail(name)),
    [form],
  );

  const formForFields = useMemo(
    () => ({
      ...form,
      setValue: handleInputChange,
      blur: validateField,
      blurEmail: handleEmailBlur,
    }),
    [form, handleInputChange, validateField, handleEmailBlur],
  );

  const toggleEdit = () => {
    setEdit(!edit);
    setDisableSubmit(!edit);
  };

  const cancelEdit = () => {
    reset(props.locationSettingsAttributes);
    props.setLocation({ ...locationInitialValue.current });
    props.setFormKey(Math.random());
    setEdit(true);
  };

  const persist = async (attributes) =>
    ConfiguredIntegrationsApi.updateConfiguredIntegration(
      props.configuredIntegration.id,
      {
        location_settings_attributes: attributes,
        additional_details: additionalDetails,
      },
    );

  const refreshFromServer = async () => {
    const { data } =
      await ConfiguredIntegrationsApi.fetchConfiguredIntegrationsByUuid(
        props.configuredIntegration.id,
      );

    if (linkedValuesLoaded) setLinkedSourceData(null);

    if (data) {
      // Keeps every schema field, as the page does, since the server omits blanks.
      const stored = attributesToValues(
        data.attributes.location_settings_attributes,
      );
      const saved = props.locationSettingsAttributes.map(({ name }) => ({
        name,
        value: stored[name] ?? '',
      }));
      setSavedAttributes(saved);
      reset(saved);
      // Cancel and its remount read the parent's copy, so it must hold the save.
      props.setLocation((prev) => ({
        ...prev,
        attributes: {
          ...prev.attributes,
          location_settings_attributes: saved,
          additional_details:
            'additional_details' in data.attributes
              ? data.attributes.additional_details
              : additionalDetails,
        },
      }));
    }
  };

  const handleSubmit = async () => {
    setDisableSubmit(true);
    setApiError([]);

    const { data, errors } = await persist(form.buildAttributes());

    if (!data) {
      notifyResult(
        'warning',
        `Failed to update ${props.configuredIntegration.attributes.name}.`,
      );
      setApiError(errors);
      setDisableSubmit(false);
      return;
    }

    await refreshFromServer();
    toggleEdit();
    notifyResult(
      'success',
      `${props.configuredIntegration.attributes.name} was successfully updated.`,
    );
  };

  const handleSaveAndPropagate = async () => {
    setDisableSubmit(true);

    const opened = await propagate.open(form.buildAttributes());

    if (!opened) setDisableSubmit(false);
  };

  const handlePropagateConfirm = async (bindingMap) => {
    propagate.setSaving(true);

    const { data, errors } = await persist(propagate.pendingAttributes);

    if (!data) {
      notifyResult(
        'warning',
        `Failed to update ${props.configuredIntegration.attributes.name}.`,
      );
      setApiError(errors || []);
      propagate.setSaving(false);
      setDisableSubmit(false);
      return;
    }

    const results = await propagate.propagateTo(bindingMap);
    const failed = results.filter((r) => r.errors);

    await refreshFromServer();

    propagate.setVisible(false);
    propagate.setSaving(false);
    toggleEdit();

    if (failed.length > 0) {
      notifyResult(
        'warning',
        `Settings saved for ${props.configuredIntegration.attributes.name}, but ${failed.length} location(s) failed to update.`,
      );
    } else {
      notifyResult(
        'success',
        `${props.configuredIntegration.attributes.name} and ${propagate.targets.length} other location(s) were successfully updated.`,
      );
    }
  };

  const handleSchemaDetailsModalOpen = (commandName) => {
    setSchemasContent(
      buildSchemaContent(
        commandName,
        schemasByCommand,
        props.integration.attributes.schemas,
      ),
    );
    setSchemaDetailsModalVisibility(true);
  };

  return (
    <>
      <SchemaDetailsModal
        content={schemasContent}
        visibility={schemaDetailsModalVisibility}
        handleVisibility={setSchemaDetailsModalVisibility}
      />
      <PropagateSettingsModal
        visible={propagate.visible}
        onClose={() => {
          propagate.close();
          setDisableSubmit(false);
        }}
        onConfirm={handlePropagateConfirm}
        sourceAttributes={propagate.pendingAttributes || []}
        targetConfiguredIntegrations={propagate.targets}
        targetIntegrationNames={propagate.integrationNames}
        sourceIntegrationName={propagate.sourceIntegrationName}
        sourceIntegrationUuid={props.integration?.id || ''}
        sourceDealerName={props.configuredIntegration.attributes.name}
        sourceSrmAccountId={
          props.configuredIntegration.attributes.srm_account_id
        }
        allSharedSettings={props.allSharedSettings}
        loading={propagate.loading}
        saving={propagate.saving}
      />
      <Grid.Container>
        <Grid.Row>
          <Grid.Column>
            <IntegrationForm>
              <Grid.Container>
                <FormHeader
                  integrationName={props?.integration?.attributes?.name}
                  readOnly={edit}
                  onEdit={toggleEdit}
                  linkedSourceData={
                    linkedValuesLoaded ? null : linkedSourceData
                  }
                  onLoadLinkedValues={loadLinkedValues}
                  onDismissLinkedValues={() => setLinkedSourceData(null)}
                  apiErrors={apiError}
                  onOpenSchemaDetails={handleSchemaDetailsModalOpen}
                />

                <SchemaSections
                  schema={props.locationSettingsSchema}
                  form={formForFields}
                  readOnly={edit}
                  texts={texts}
                  encryptText={passwordMask}
                  onOpenSchemaDetails={handleSchemaDetailsModalOpen}
                  onHeaderValidate={handleHeaderValidate}
                />

                <FormFooter
                  additionalDetails={additionalDetails}
                  onAdditionalDetailsChange={setAdditionalDetails}
                  readOnly={edit}
                  saveDisabled={disableSubmit || !isConfigured || !valuesValid}
                  propagateDisabled={
                    disableSubmit ||
                    !isConfigured ||
                    !valuesValid ||
                    propagate.loading ||
                    !props.configuredIntegration.attributes?.enabled
                  }
                  canPropagate={inSharedSetting}
                  onSave={handleSubmit}
                  onSaveAndPropagate={handleSaveAndPropagate}
                  onCancel={cancelEdit}
                />
              </Grid.Container>
            </IntegrationForm>
          </Grid.Column>
        </Grid.Row>
      </Grid.Container>
    </>
  );
};

export default Form;

Form.propTypes = {
  locationSettingsSchema: PropTypes.oneOfType([PropTypes.object]).isRequired,
  configuredIntegration: PropTypes.oneOfType([PropTypes.object]).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  locationSettingsAttributes: PropTypes.any.isRequired,
  setLocation: PropTypes.func.isRequired,
  setFormKey: PropTypes.func.isRequired,
  setToggleDisabled: PropTypes.func.isRequired,
  integration: PropTypes.oneOfType([PropTypes.object]).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  allSharedSettings: PropTypes.array.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  linkedSourceData: PropTypes.object,
  setLinkedSourceData: PropTypes.func,
};
